import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export interface ResizeHandleProps {
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: (e: ReactPointerEvent) => void;
  onPointerCancel: (e: ReactPointerEvent) => void;
}

export interface ResizablePanel {
  width: number;
  /** Spread onto the drag handle element; style it however the surface needs. */
  handleProps: ResizeHandleProps;
  reset: () => void;
}

/**
 * Width state for a drag-resizable side panel.
 *
 * `side` is which edge of the layout the panel is docked to, which determines the sign of the
 * drag: a panel docked right grows as the pointer moves left. Getting that backwards is the
 * classic bug here, so it's expressed as a property of the panel rather than left to each caller.
 *
 * Pointer capture is taken on the handle so a fast drag that outruns the 4px hit area keeps
 * resizing instead of stalling the moment the cursor leaves it.
 */
export function useResizablePanel(options: {
  initial: number;
  min: number;
  max: number;
  side?: 'left' | 'right';
  /** When set, the chosen width survives reloads — a panel you must re-widen every visit isn't resizable in any useful sense. */
  storageKey?: string;
}): ResizablePanel {
  const { initial, min, max, side = 'left', storageKey } = options;

  const clamp = useCallback((value: number) => Math.max(min, Math.min(max, value)), [min, max]);

  const [width, setWidth] = useState(() => {
    if (!storageKey) return initial;
    // Clamped on read as well as write: the bounds may have changed since the value was stored.
    const stored = Number(window.localStorage.getItem(storageKey));
    return Number.isFinite(stored) && stored > 0 ? Math.max(min, Math.min(max, stored)) : initial;
  });

  const drag = useRef<{ startX: number; startW: number } | null>(null);

  const commit = useCallback(
    (next: number) => {
      setWidth(next);
      if (storageKey) window.localStorage.setItem(storageKey, String(next));
    },
    [storageKey],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      drag.current = { startX: e.clientX, startW: width };
    },
    [width],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (!drag.current) return;
      const delta = side === 'right' ? drag.current.startX - e.clientX : e.clientX - drag.current.startX;
      commit(clamp(drag.current.startW + delta));
    },
    [side, clamp, commit],
  );

  const endDrag = useCallback((e: ReactPointerEvent) => {
    const target = e.currentTarget as HTMLElement;
    if (target.hasPointerCapture?.(e.pointerId)) target.releasePointerCapture(e.pointerId);
    drag.current = null;
  }, []);

  const reset = useCallback(() => commit(initial), [commit, initial]);

  return {
    width,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
    reset,
  };
}
